/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import { TObject } from "constants/interfaces";

import useTryCatch from "hooks/useTryCatch";
import { useUI } from "state";
import Modal from "components/ui/Modal";
import Text from "components/ui/Text";
import { Input } from "components/ui/Input";
import Label from "components/ui/Label";

interface IModalDelete {
  title?: string;
  onDelete: Awaited<any>;
  onClose?: () => void;
  note?: string;
  deleteArgs?: TObject;
  cancelArgs?: TObject;
  hasCheckbox?: boolean;
  checkboxLabel?: string;
}

const ModalDelete = ({
  title = "Are you sure?",
  onDelete,
  onClose,
  note,
  deleteArgs,
  cancelArgs,
  hasCheckbox = false,
  checkboxLabel = "I understand the consequences",
}: IModalDelete) => {
  const { closeModal } = useUI();
  const [isLoading, setLoading] = useState(false);
  const [isChecked, setChecked] = useState<boolean>(hasCheckbox ? false : true);

  const deleteFn = useTryCatch(
    async () => {
      await onDelete?.();
      onClose?.();
      closeModal();
    },
    {
      setLoading,
      onFinally: () => {
        onClose?.();
        closeModal();
      },
    },
  );

  return (
    <Modal.Container className="max-w-xs !min-w-full" test-id="sDTcjErG">
      <Modal.Header
        className="p-4 pb-0"
        title={title}
        hasBorder={false}
        hasPadding={false}
        test-id="3KaqH3UO"
      />

      <Modal.Body test-id="GcuG3s9-">
        <Text test-id="pPq2MLpX" className="text-sm text-light-gray">
          {note || "Please note that all related data will be erased."}
        </Text>

        {hasCheckbox && (
          <div className="flex items-center gap-x-2 mt-4">
            <Input
              size="sm"
              type="checkbox"
              className="mr-2"
              id="delete-checkbox"
              checked={isChecked}
              onChange={() => setChecked((prev) => !prev)}
            />

            <Label
              isBold={false}
              size="small"
              htmlFor="delete-checkbox"
              variant="dark"
            >
              {checkboxLabel}
            </Label>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer
        buttons={[
          {
            size: "large",
            variant: "secondary",
            className: "w-full",
            onClick: onClose ? onClose : closeModal,
            label: "Cancel",
            ...cancelArgs,
          },
          {
            variant: "alert",
            size: "large",
            isLoading,
            onClick: deleteFn,
            label: "Delete",
            disabled: !isChecked,
            ...deleteArgs,
          },
        ]}
      />
    </Modal.Container>
  );
};

export default ModalDelete;
